import React, { useState, useEffect, useRef } from "react";
import { Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaShoppingBag, FaArrowRight } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import { getLakayUser, getProductsF, getProductSearch } from "../auth/apiProduct";
import { readHistorySearchLS, getFirstIds } from "../auth";
import ButtonPagination from "../pageInside/ButtonPagination";
import Card from '../pageInside/Card';
import elakayEmptyBox from "../Layout/icon/elakayEmptyBox.png";

// ✅ CSS externe — contient toutes les animations via animations.library.css
// gradientWave  → resultAnimated=true  sur ResultPagination
// gradientShift → gridAnimated=true    sur ProductGrid wrapper
// fadeInUp      → animateCards=true    sur chaque carte (stagger nth-child)
// shimmer       → skeleton des cartes vides
// float         → icône pgp-no-results
import './ProductGridPage.css';
import { elakayAutoText, elakayPluralRule, getElakayLanguageContext } from "./dataElakay";
import { CARD_MODE_STORAGE_KEY, CARD_MODE_EVENT, getCardColumns } from "../../utils/cardDisplaySettings";
import { resolveBuyerLocation, BUYER_LOCATION_EVENT } from "../../utils/buyerLocation";
import { getProductGridLimit } from "../../utils/paginationSettings";
import InfiniteScrollLoader from "./InfiniteScrollLoader";
import { mergeUniqueById, normalizeInfiniteScrollPageId, resolveInfiniteScroll } from "../../utils/infiniteScrollSettings";
import { resolveProductMeta, resolveProductRows } from "../../utils/productResponse";
import { buildProductPagination } from "../../utils/productPagination";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
// Current language source: the app-wide one (always up to date after a language switch), else
// the infoSource this module last rendered with.
const __elakayLanguageSource = () => getElakayLanguageContext?.() || __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayLanguageSource()?.laguangeElakay?.("frontElakayHardcodedText", __elakayLanguageSource().languageAPI)?.["productGridPage"]?.[key] || fallback;

// ============================================
// HELPER
// ============================================
const getRandomColor = () => {
    const colors = [
        'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
        'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
        'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
        'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
        'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
        'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
    ];
    return colors[Math.floor(Math.random() * colors.length)];
};

// ============================================
// RESULT PAGINATION COMPONENT
// ============================================
const ResultPagination = ({
    prodCat          = "",
    skip             = 0,
    limit            = 12,
    size             = 0,
    siseProduct      = 0,
    infoSource,
    style            = "minimal",
    customBackground = null,
    icon             = null,
    showIcon         = true,
    showBadge        = true,
    showPageInfo     = true,
    showTitle        = true,
    animated         = false,   // true → classe .animated → gradientWave
    className        = "",
    textColor        = null,
    textSize         = null,
    seeMoreLink      = null,    // route → "See More" link on the right of the bar (e.g. Home → /new-product)
}) => {
    __setElakayInfoSource(infoSource);
    const pagination = buildProductPagination({ skip, limit, size, totalProducts: siseProduct });
    const { start, end, currentPage, totalPages, totalProducts: realTotal } = pagination;
    const hasData = pagination.size > 0;

    const ofText    = infoSource?.laguangeElakay?.('label', infoSource.languageAPI)?.of    || "of";
    // "1 item" / "2 items" with the active language's plural rules (label.item / label.items).
    const itemsText = (count) => (elakayPluralRule(count) === "one"
        ? infoSource?.laguangeElakay?.('label', infoSource.languageAPI)?.item || "item"
        : infoSource?.laguangeElakay?.('label', infoSource.languageAPI)?.items || "items");

    const styleClass  = customBackground ? 'style-custom' : `style-${style}`;
    const customStyle = {
        ...(customBackground ? { background: customBackground } : {}),
        ...(textColor ? { color: textColor } : {}),
        ...(textSize ? { fontSize: textSize } : {}),
    };

    return (
        <div
            className={`pgp-result-bar ${styleClass} ${animated ? 'animated' : ''} ${className}`.trim()}
            style={customStyle}
        >
            <div className="pgp-result-left">
                {showIcon && (
                    <div className="pgp-result-icon">
                        {icon || <FaShoppingBag />}
                    </div>
                )}
                <div className="pgp-result-content">
                    {showTitle ? <h5>
                        {prodCat || elakayAutoText("h_fe0a091fdbfb3e", "Products")}
                        {showBadge && hasData && (
                            <span className="pgp-result-badge">
                                <HiSparkles size={14} />
                                {realTotal} {itemsText(realTotal)}
                            </span>
                        )}
                    </h5> : null}
                    <p>
                        {hasData
                            ? `${start} - ${end} ${ofText} ${realTotal} ${itemsText(realTotal)}`
                            : `0 ${itemsText(0)}`
                        }
                    </p>
                </div>
            </div>

            {(showPageInfo || seeMoreLink) && (
                <div className="pgp-result-right">
                    {showPageInfo && (
                        <div className="pgp-page-info">{__elakayText("t002", "Page")} {currentPage} / {totalPages}
                        </div>
                    )}
                    {seeMoreLink && (
                        <Link to={seeMoreLink} className="pgp-see-more">
                            {infoSource?.laguangeElakay?.('Link', infoSource.languageAPI)?.seeMore || "See More"}
                            <FaArrowRight size={12} />
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};

// Standard section names an administrator may type as the result title (Animation Settings), mapped
// to the translated homePage labels; the comparison ignores case and surrounding spaces.
const STANDARD_TITLES = {
    "new products": "newProduct", "new arrivals": "newProduct",
    "best sellers": "bestSellers",
    "today's deal": "dealDay", "today's deals": "dealDay", "deal of the day": "dealDay", "today deals": "dealDay",
};
const configuredTitleText = (title, infoSource) => {
    const raw = String(title || "").trim();
    if (!raw) return "";
    const key = STANDARD_TITLES[raw.toLowerCase()];
    if (raw.toLowerCase() === "products") return elakayAutoText("h_fe0a091fdbfb3e", "Products");
    return (key && infoSource?.laguangeElakay?.('homePage', infoSource.languageAPI)?.[key]) || raw;
};

export const resolveGridDataPage = ({ sectionId, facet, page = 'home' }) => {
    const facetValue = Array.isArray(facet) ? facet[0] : facet;
    const gridIdentity = sectionId || facetValue || page;
    const gridPageMap = {
        // Keep the historical spelling accepted by saved AnimationSettings.
        userVIewGrid: 'userView',
        userViewGrid: 'userView',
        userView: 'userView',
        bestSellersGrid: 'bestSellers',
        bestSellers: 'bestSellers',
        dealTodaysGrid: 'dealTodays',
        dealTodays: 'dealTodays',
        productGrid: page || 'home',
        __PRODUCTS_BASE__: page || 'home',
    };
    return gridPageMap[gridIdentity] || page;
};

// ============================================
// PRODUCT GRID COMPONENT
// ============================================
const ProductGrid = ({
    productsByArrival = [],
    loading          = false,
    Card,
    laguangeElakay,
    infoSource,
    promoMaker,
    btnShopPromo,
    refShopName,
    elakayEmptyBox,
    style            = "minimal",
    customBackground = null,
    columns          = { xs: 2, sm: 2, md: 3, lg: 4 },
    emptyCount       = 12,
    animated         = false,    // true → classe .animated → gradientShift
    animateCards     = false,    // true → classe .animate-appear → fadeInUp stagger
    className        = "",
    textColor        = null,
    textSize         = null,
    cardMode         = 'portrait',
}) => {
    const styleClass    = customBackground ? 'style-custom' : `style-${style}`;
    const animatedClass = animated ? 'animated' : '';
    const customStyle   = {
        ...(customBackground ? { background: customBackground } : {}),
        ...(textColor ? { color: textColor } : {}),
        ...(textSize ? { fontSize: textSize } : {}),
    };

    const renderCards = () => {
        const cards = [];

        // ── Produits réels ──────────────────────────────────────────────
        productsByArrival?.forEach((product, i) => {
            cards.push(
                <div
                    // animate-appear → fadeInUp + stagger nth-child (ProductGridPage.css)
                    className={`col pgp-product-col ${animateCards ? 'animate-appear' : ''}`}
                    key={product._id || i}
                >
                    <Card
                        laguangeElakay={laguangeElakay}
                        infoSource={infoSource}
                        product={product}
                        classCard0="pgp-card-shell"
                        // showShopV={false}
                        promoMaker={promoMaker}
                        btnShopPromo={btnShopPromo}
                        refShopName={refShopName}
                        displayMode={cardMode}
                        isGridPage={true}
                    />
                </div>
            );
        });

        // ── Cartes vides avec skeleton ──────────────────────────────────
        // pgp-skeleton → animation shimmer (ProductGridPage.css)
        if (loading && productsByArrival?.length === 0) {
            for (let f = 0; f < emptyCount; f++) {
                cards.push(
                    <div className="col pgp-product-col" key={`filler-${f}`}>
                        <div className="pgp-empty-card">
                            <div
                                className="pgp-empty-card-image"
                                style={{ background: getRandomColor() }}
                            >
                                {elakayEmptyBox && (
                                    <img
                                        src={elakayEmptyBox}
                                        alt={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).productGridPage.t001}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                )}
                            </div>
                            <div className="pgp-empty-card-body">
                                <div className="pgp-skeleton title"></div>
                                <div className="pgp-skeleton subtitle"></div>
                                <div className="pgp-skeleton price"></div>
                            </div>
                        </div>
                    </div>
                );
            }
        }

        return cards;
    };

    return (
        <div
            className={`pgp-grid-wrapper ${styleClass} ${animatedClass} ${className}`.trim()}
            style={customStyle}
        >
            <Row
                className="pgp-product-row"
                xs={columns.xs}
                sm={columns.sm}
                md={columns.md}
                lg={columns.lg}
            >
                {renderCards()}
            </Row>
        </div>
    );
};

export const ProductGridSkeleton = ({ count = 4, columns = { xs: 2, sm: 2, md: 3, lg: 4 } }) => (
    <div className="pgp-grid-wrapper pgp-infinite-skeleton" aria-hidden="true">
        <Row className="pgp-product-row" xs={columns.xs} sm={columns.sm} md={columns.md} lg={columns.lg}>
            {Array.from({ length: count }, (_, index) => (
                <div className="col pgp-product-col" key={index}>
                    <div className="pgp-empty-card">
                        <div className="pgp-empty-card-image pgp-skeleton-block" />
                        <div className="pgp-empty-card-body">
                            <div className="pgp-skeleton title" />
                            <div className="pgp-skeleton subtitle" />
                            <div className="pgp-skeleton price" />
                        </div>
                    </div>
                </div>
            ))}
        </Row>
    </div>
);

// ============================================
// MAIN COMPONENT — ProductGridPage
// ============================================
const ProductGridPage = ({
    prodCat            = "",
    laguangeElakay,
    infoSource,
    promoMaker         = false,
    btnShopPromo,
    refShopName,

    // ── Result bar ───────────────────────────────────────────────────
    // style: transparent|gradient|glass|dark|minimal|success|warm|cool|rose|indigo|slate|custom
    // resultAnimated=true → .animated → gradientWave (animations.library.css)
    // Valeurs par défaut (resultBackground/resultAnimated/...) lues depuis
    // AnimationSettings (infoSource.allSettings.productGrids.productGridPage) si
    // non fournies explicitement par l'appelant — voir effective* ci-dessous.
    resultStyle        = "minimal",
    resultBackground,
    resultIcon         = null,
    showResultIcon     = true,
    showResultBadge    = true,
    showResultPageInfo = true,
    resultAnimated,

    // ── Grid wrapper ─────────────────────────────────────────────────
    // style: gradient|glass|dark|minimal|warm|cool|rose|success|indigo|slate|transparent|custom
    // gridAnimated=true  → .animated  → gradientShift (animations.library.css)
    // animateCards=true  → .animate-appear → fadeInUp stagger (animations.library.css)
    gridStyle          = "minimal",
    gridBackground,
    gridAnimated,
    animateCards,

    columns,
    containerStyle     = {},
    containerClassName = "mt-3 mb-3",
    emptyCount         = 12,

    // ── API / Data ───────────────────────────────────────────────────
    myFilters          = { filters: { "categoriesID.gategoriesLink": [] } },
    sortBy             = 'createdAt',
    categoryId         = null,
    getCi,
    search             = "admin",
    userID,
    page               = 'home',
    totalKey           = null,
    facet              = [],
    sectionId          = null,

    // Data the parent's useDataLoader already fetched for the base
    // products facet on this page — used once, on the very first render,
    // to avoid firing a duplicate getProductsF call. Any subsequent
    // pagination/filter/category change still self-fetches normally (see
    // initialDataConsumedRef below). Absent/empty on pages that don't
    // provide it (unchanged, existing self-fetch behavior).
    initialData        = null,

    // Optional route for a "See More" link in the result bar (Home's
    // productGrid → /new-product, set in home.jsx). Absent → no link.
    seeMoreLink        = null,
}) => {
    // The dynamic page renderer can mount several ProductGridPage variants.
    // Resolve the section identity first so userViewGrid never falls back to
    // the generic productGrid data used by Home/New Products/Categories.
    const dataPage = resolveGridDataPage({ sectionId, facet, page });
    const infiniteSectionId = sectionId || ({
        userView: 'userViewGrid', bestSellers: 'bestSellersGrid', dealTodays: 'dealTodaysGrid',
    }[dataPage] || 'productGrid');
    const infiniteConfig = resolveInfiniteScroll(
        infoSource?.allSettings,
        normalizeInfiniteScrollPageId(page),
        infiniteSectionId,
        categoryId
    );

    // Page size ("Load More" step) — admin-configurable via AnimationSettings
    // → General → "Pagination & Limites" (productGrids.<bucket>.limit).
    // `page` picks the bucket the same way allDataLoader below picks a
    // loader: 'shopUser'/'search' get their own dedicated bucket, everything
    // else (home/ProductCatalog/newProducts/bestSellers/dealTodays/userView)
    // shares 'productGridPage' — mirrors the existing visual-settings split
    // (resultBackground/gridAnimated/...) for these same 4 buckets.
    const PRODUCT_GRID_LIMIT_BUCKET = { shopUser: 'productGridShop', search: 'productGridSearch' };
    const limitBucket = PRODUCT_GRID_LIMIT_BUCKET[page] || 'productGridPage';
    const limit = getProductGridLimit(infoSource?.allSettings, limitBucket, 12);
    const [skip,             setSkip]          = useState(0);
    const [size,             setSize]          = useState(0);
    const [siseProduct,      setSiseProduct]   = useState(0);
    const [productsByArrival, setProductsByArrival] = useState([]);
    const [loading,          setLoading]          = useState(true);
    const [loadError,        setLoadError]        = useState("");
    const requestVersionRef = useRef(0);
    const loadingMoreRef = useRef(false);
    useEffect(() => () => {
        requestVersionRef.current += 1;
        loadingMoreRef.current = false;
    }, []);

    // Location Context: the consumer's explicit Target Location (saved by the
    // LocationFilterBar in the Elakay Filters panel, Filter_fitlerP.js), then
    // only ever changed through BUYER_LOCATION_EVENT right below. Without a
    // target nothing is sent and the SERVER resolves the destination itself
    // (signed-in buyer's default address, else approximate IP location --
    // server_elakay elakay_services/locationContext.js), so the address is
    // never mistaken for a deliberate target. Every loader below sends this
    // target; the server filters regardless of what is sent.
    const [buyerLocation, setBuyerLocation] = useState(() => resolveBuyerLocation());
    // The actual "Use my default address"/"Change location" controls now
    // live in the Elakay Filters panel (Filter_fitlerP.js) -- this page still
    // owns buyerLocation (it drives every product query below), so it
    // listens for the same custom event saveTempBuyerLocation() dispatches,
    // exactly like the Card Display Mode (portrait/landscape) sync below.
    useEffect(() => {
        const updateLocation = (e) => setBuyerLocation(e.detail || null);
        window.addEventListener(BUYER_LOCATION_EVENT, updateLocation);
        return () => window.removeEventListener(BUYER_LOCATION_EVENT, updateLocation);
    }, []);

    // Card Display Mode (Portrait/Landscape) — Grid page. Préférence globale
    // (localStorage), poussée en direct par SideDrawer1.js via un événement
    // custom (voir utils/cardDisplaySettings.js).
    const [cardMode, setCardMode] = useState(
        () => (typeof window !== 'undefined' && window.localStorage.getItem(CARD_MODE_STORAGE_KEY)) || 'portrait'
    );
    useEffect(() => {
        const updateMode = (e) => {
            setCardMode(e.detail?.mode || window.localStorage.getItem(CARD_MODE_STORAGE_KEY) || 'portrait');
        };
        window.addEventListener(CARD_MODE_EVENT, updateMode);
        return () => window.removeEventListener(CARD_MODE_EVENT, updateMode);
    }, []);

    // Colonnes par appareil — dépendent du mode actif (portrait/landscape),
    // une prop explicite passée par l'appelant reste prioritaire.
    const effectiveColumns = getCardColumns(cardMode, infoSource?.allSettings?.cardDisplaySettings, columns);

    // Apparence configurable depuis AnimationSettings (page "Pages produits &
    // commandes"). Une prop explicite passée par l'appelant reste prioritaire.
    const gridSettings = infoSource?.allSettings?.productGrids?.productGridPage || {};
    // prodCat (explicit prop, e.g. a category name on category pages) always
    // wins; otherwise the admin-configurable title (AnimationSettings →
    // Pages produits & commandes → ProductGridPage → "Titre affiché")
    // replaces the hardcoded "Products" fallback baked into ResultPagination.
    // The admin title is one stored string (single language): when it is one of Elakay's standard
    // section names it is shown in the active language, anything else is shown exactly as typed.
    const existingResultTitle = prodCat || configuredTitleText(gridSettings.resultTitle, infoSource) || "";
    const effectiveResultTitle = infiniteConfig.titleMode === 'custom'
        ? infiniteConfig.customTitle
        : existingResultTitle;
    const showInfiniteTitle = infiniteConfig.titleMode !== 'hidden';
    const effectiveResultBackground = resultBackground ?? gridSettings.resultBackground ?? null;
    const effectiveResultAnimated   = resultAnimated   ?? gridSettings.resultAnimated   ?? false;
    const effectiveGridBackground   = gridBackground   ?? gridSettings.gridBackground   ?? null;
    const effectiveGridAnimated     = gridAnimated     ?? gridSettings.gridAnimated     ?? false;
    const effectiveAnimateCards     = animateCards     ?? gridSettings.animateCards     ?? false;

    const divProdGridPage = useRef(null);

    const scrollToParagraph = (ref) => {
        if (ref && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // ── Loader API selon page ─────────────────────────────────────────
    const allDataLoader = (toSkip, currentSortBy = 'createdAt', currentPage = 'home') => {

        if (['home', 'ProductCatalog', 'newProducts', 'bestSellers', 'dealTodays', 'userView'].includes(currentPage)) {
            const userViewed    = getFirstIds(readHistorySearchLS('userView'));
            const historySearch = readHistorySearchLS('historySearch');
            const facetMap = {
                newProducts: ['newProducts'],
                bestSellers: ['bestSellers'],
                dealTodays:  ['dealTodays'],
                userView:    ['userView'],
            };
            const resolvedFacet = facetMap[currentPage] || [];
            return getProductsF(toSkip, limit, currentSortBy, {
                filters: myFilters.filters,
                userViewed,
                historySearch,
                facet: resolvedFacet,
            }, buyerLocation);

        } else if (['shopUser'].includes(currentPage)) {
            return getLakayUser(userID, toSkip, limit, currentSortBy, getCi, search, {
                filters: myFilters.filters,
                facet: ["shopLakayUser"],
            }, buyerLocation);

        } else if (['search'].includes(currentPage)) {
            return getProductSearch(toSkip, limit, "createdAt", getCi, search, myFilters.filters, buyerLocation);
        }
    };

    // ── Mapping data → state ──────────────────────────────────────────
    const setData = (data, currentPage = 'home', append = false) => {
        const productKeys = {
            newProducts: ['newProducts', 'productsByArrival'],
            dealTodays:  ['dealTodays', 'dealToDayProducts'],
            userView:    ['userView', 'userViewedProduct'],
            bestSellers: ['bestSellers', 'productsBySell'],
        };
        const totalMap = {
            userView:    ['userViewTotal'],
            bestSellers: ['bestSellersTotal'],
            dealTodays:  ['dealTodaysTotal'],
            newProducts: ['newProductsTotal'],
        };
        const products = resolveProductRows(data, productKeys[currentPage] || []);
        const { size: resolvedSize, total: resolvedTotal } = resolveProductMeta(data, products, {
            totalKeys: [totalKey, ...(totalMap[currentPage] || [])].filter(Boolean),
        });
        setProductsByArrival((current) => append ? mergeUniqueById(current, products) : products);
        setSize(resolvedSize);
        setSiseProduct(resolvedTotal);
        setLoadError(resolvedTotal > 0 && products.length === 0
            ? "La réponse annonce des produits, mais ne contient aucune ligne exploitable."
            : "");
        setLoading(false);
    };

    const handleData = (data, currentPage, toSkip = 0, append = false, requestVersion = requestVersionRef.current) => {
        if (requestVersion !== requestVersionRef.current) return;
        if (!data || data.error) {
            setLoading(false);
            loadingMoreRef.current = false;
            setLoadError(data?.error || "Impossible de charger les produits.");
            return;
        }
        setData(data, currentPage, append);
        setSkip(toSkip);
        loadingMoreRef.current = false;
    };

    // ── Init / refetch on category or filter change ────────────────────
    // Array.isArray (not .length) so a legitimately-empty first page
    // (e.g. empty category/search) is still honored as "already fetched
    // by the parent" instead of triggering a duplicate self-fetch here.
    //
    // categoryId/myFiltersKey are included below (bug fix): without them,
    // this effect never re-ran when the parent's route stayed mounted and
    // only its :categoryId param changed (React Router doesn't remount on
    // a param-only change) -- the grid kept showing the previous
    // category's products at the previous category's skip. handleData
    // defaults toSkip to 0, so re-running this effect also correctly
    // resets pagination back to page 1 for the new category/filters.
    const myFiltersKey = JSON.stringify(myFilters?.filters || {});
    const initialDataConsumedRef = useRef(false);
    useEffect(() => {
        const requestVersion = ++requestVersionRef.current;
        loadingMoreRef.current = false;
        const initialProductsMap = {
            userView: initialData?.userViewedProduct ?? initialData?.userView,
            bestSellers: initialData?.productsBySell ?? initialData?.bestSellers,
            dealTodays: initialData?.dealToDayProducts ?? initialData?.dealTodays,
        };
        const initialProducts = initialProductsMap[dataPage] ?? initialData?.products;
        const initialTotal = Number(initialData?.totalProducts ?? initialData?.siseProduct ?? 0);
        // Une liste initiale vide avec un total positif est un contrat
        // incohérent (cas observé en production). Ne pas la considérer comme
        // chargée : le self-loader refait alors la requête et normalise items/
        // results/data.products au lieu d'afficher 12 skeletons indéfiniment.
        if (!initialDataConsumedRef.current && Array.isArray(initialProducts) && !(initialProducts.length === 0 && initialTotal > 0)) {
            initialDataConsumedRef.current = true;
            handleData({ ...initialData, products: initialProducts }, dataPage, 0, false, requestVersion);
            return;
        }
        initialDataConsumedRef.current = true;
        if (infiniteConfig.enabled) setProductsByArrival([]);
        setSkip(0);
        setLoading(true);
        setLoadError("");
        allDataLoader(0, sortBy, dataPage)
            ?.then(data => handleData(data, dataPage, 0, false, requestVersion))
            .catch(error => {
                if (requestVersion !== requestVersionRef.current) return;
                setLoading(false);
                loadingMoreRef.current = false;
                setLoadError(error?.message || "Impossible de charger les produits.");
            });
    // buyerLocation is included so LocationFilterBar changes reset pagination
    // and refetch (mission 2.8) -- everything else about the dep list is
    // unchanged existing behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataPage, userID, getCi, search, buyerLocation, categoryId, myFiltersKey, limit, infiniteConfig.enabled]);

    // ── Pagination ────────────────────────────────────────────────────
    const loadMore = () => {
        if (loadingMoreRef.current) return;
        const append = infiniteConfig.enabled;
        const toSkip = append ? productsByArrival.length : skip + limit;
        if (!append) scrollToParagraph(divProdGridPage);
        loadingMoreRef.current = true;
        const requestVersion = requestVersionRef.current;
        setLoading(true);
        setLoadError("");
        allDataLoader(toSkip, sortBy, dataPage)
            ?.then(data => handleData(data, dataPage, toSkip, append, requestVersion))
            .catch(error => {
                if (requestVersion !== requestVersionRef.current) return;
                setLoading(false);
                loadingMoreRef.current = false;
                setLoadError(error?.message || "Impossible de charger les produits.");
            });
    };

    const loadMore0 = () => {
        const toSkip = Math.max(0, skip - limit);
        scrollToParagraph(divProdGridPage);
        setLoading(true);
        setLoadError("");
        allDataLoader(toSkip, sortBy, dataPage)
            ?.then(data => handleData(data, dataPage, toSkip))
            .catch(error => { setLoading(false); setLoadError(error?.message || "Impossible de charger les produits."); });
    };

    // ── Render ────────────────────────────────────────────────────────
    return (
        <div
            ref={divProdGridPage}
            className={containerClassName}
            style={containerStyle}
        >
            <ResultPagination
                prodCat={effectiveResultTitle}
                showTitle={showInfiniteTitle}
                skip={skip}
                limit={limit}
                size={size}
                siseProduct={siseProduct}
                infoSource={infoSource}
                style={resultStyle}
                customBackground={effectiveResultBackground}
                icon={resultIcon}
                showIcon={showResultIcon}
                showBadge={showResultBadge}
                showPageInfo={showResultPageInfo}
                animated={effectiveResultAnimated}
                textColor={gridSettings.resultTextColor}
                textSize={gridSettings.resultTextSize}
                seeMoreLink={seeMoreLink}
            />

            <ProductGrid
                productsByArrival={productsByArrival}
                loading={loading}
                Card={Card}
                laguangeElakay={laguangeElakay}
                infoSource={infoSource}
                promoMaker={promoMaker}
                btnShopPromo={btnShopPromo}
                refShopName={refShopName}
                elakayEmptyBox={elakayEmptyBox}
                style={gridStyle}
                customBackground={effectiveGridBackground}
                columns={effectiveColumns}
                emptyCount={emptyCount}
                animated={effectiveGridAnimated}
                animateCards={effectiveAnimateCards}
                textColor={gridSettings.gridTextColor}
                textSize={gridSettings.gridTextSize}
                cardMode={cardMode}
            />

            <InfiniteScrollLoader
                enabled={infiniteConfig.enabled}
                hasMore={productsByArrival.length < siseProduct && size > 0}
                loading={loading && productsByArrival.length > 0}
                error={productsByArrival.length > 0 ? loadError : ""}
                onLoadMore={loadMore}
                onRetry={loadMore}
                skeleton={<ProductGridSkeleton count={Math.min(limit, 4)} columns={effectiveColumns} />}
            />

            {!loading && productsByArrival.length === 0 && (
                <div className={`pgp-data-state ${loadError ? 'is-error' : ''}`} role={loadError ? "alert" : "status"}>
                    {loadError || elakayAutoText("h_msl_empty", "No Products Available")}
                </div>
            )}

            {ButtonPagination && !infiniteConfig.enabled && (
                <div 
                // className="pgp-pagination-wrapper"
                >
                    <ButtonPagination
                        infoSource={infoSource}
                        size={size}
                        skip={skip}
                        limit={limit}
                        siseProduct={siseProduct}
                        loadMore={loadMore}
                        loadMore0={loadMore0}
                    />
                </div>
            )}
        </div>
    );
};

export { ResultPagination, ProductGrid };
export default ProductGridPage;
